import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// Ledger Forge (level 70) — the Logic rung.
//
// Nothing here polls and nothing runs on a clock: the ledger only changes when the player changes it. The
// draft is held locally so typing stays instant, and filed to the server on a short debounce — the server
// is the only thing that computes the residuals, so a second copy of the audit cannot drift from the one
// that grades the attempt.
//
// WHAT THIS VIEW SHOWS, AND WHY IT STOPS WHERE IT DOES. The residuals are the state of the draft the
// player wrote — how far each notarised subtotal is out, how far the digest is out — not a list of drafts
// that would work. That distinction is the line this ladder draws, and it was measured rather than
// asserted: a climber allowed to nudge one line at a time gets past the audit on 0% of boards, because
// every single-line nudge breaks a subtotal and the objective has a wall around every legal ledger. What
// does work is realising that the local split is a LEVER on the digest, which is the thing worth learning.

interface EntryV {
  name: string;
  segment: number;
  weight: number;
  notarised: number;
  amount: number;
  delta: number;
  tolerance: number;
  isYours: boolean;
  overTolerance: boolean;
}

interface SegmentV {
  name: string;
  notarised: number;
  delta: number;
  mine: boolean;
}

interface Challenge {
  started: boolean;
  skim: number;
  skimDelta: number;
  sealNotarised: number;
  sealDelta: number;
  sealTolerance: number;
  touched: number;
  editBudget: number;
  yourIndex: number;
  yourSegment: number;
  ok: boolean;
  timeLimitSec: number;
  readingAllowanceSec: number;
  segments: SegmentV[];
  entries: EntryV[];
}

const DEBOUNCE_MS = 320;

export default function LedgerForgeView({
  challenge, onAct, onSubmit, setPendingSubmission,
}: MinigameViewProps<Challenge, Challenge>) {
  const [live, setLive] = useState<Challenge | null>(challenge ?? null);
  const [amounts, setAmounts] = useState<number[]>([]);
  const [note, setNote] = useState<string | null>(null);

  const sendingRef = useRef(false);
  const pendingRef = useRef<number[] | null>(null);

  const entries = useMemo(() => live?.entries ?? [], [live]);
  const started = live?.started ?? false;

  useEffect(() => {
    setLive(challenge ?? null);
    setAmounts(challenge?.entries?.map(e => e.amount) ?? []);
    setNote(null);
    sendingRef.current = false;
    pendingRef.current = null;
  }, [challenge]);

  const send = useCallback(async (action: unknown) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    try {
      const next = await onAct(action);
      if (next) {
        setLive(next);
        if (next.entries?.length && amounts.length === 0) setAmounts(next.entries.map(e => e.amount));
      }
    } finally {
      sendingRef.current = false;
      // Whatever the player typed while a draft was in flight is filed next, so the server's residuals
      // always describe the ledger as it now stands.
      const queued = pendingRef.current;
      if (queued) { pendingRef.current = null; void send({ kind: 'draft', amounts: queued }); }
    }
  }, [onAct, amounts.length]);

  // The host grades whatever is pending if the clock runs out, so the work is never thrown away.
  useEffect(() => {
    if (amounts.length > 0) setPendingSubmission({ amounts });
  }, [amounts, setPendingSubmission]);

  // Typing stays local; the audit is asked for a moment later.
  useEffect(() => {
    if (!started || amounts.length === 0) return;
    const id = setTimeout(() => {
      if (sendingRef.current) { pendingRef.current = amounts; return; }
      void send({ kind: 'draft', amounts });
    }, DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [amounts, started, send]);

  const setAt = (i: number, v: number) =>
    setAmounts(a => a.map((x, j) => (j === i ? Math.max(1, Math.min(9_999_999, v)) : x)));

  const mono: React.CSSProperties = { fontFamily: 'monospace', fontSize: '0.8rem' };
  const small: React.CSSProperties = { ...mono, fontSize: '0.72rem' };

  if (!started) {
    return (
      <div style={{ fontFamily: 'monospace' }}>
        <div style={{ border: '1px solid #232833', borderRadius: 4, padding: '0.9rem', background: '#0d1117' }}>
          <p style={{ ...mono, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            Fifteen ledger lines in three notarised segments. Move{' '}
            <strong>{live?.skim ?? 0} credits</strong> onto your own line and file the books so the audit
            finds nothing.
          </p>
          <p style={{ ...mono, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            Two things were notarised and both have to come back identical. Every{' '}
            <strong>segment subtotal</strong> — so your gain has to be absorbed by other lines{' '}
            <em>inside your own segment</em>. And the <strong>digest</strong>, a weighted sum of every
            amount with the weights printed against each line — so moving credits between positions of
            different weight leaves a residual, and that residual has to be squared away too.
          </p>
          <p style={{ ...mono, color: '#60a5fa', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            Here is the part worth working out. The digest can only be repaired by shifting amount between
            two lines, and doing that without breaking a second subtotal means both lines must sit in the{' '}
            <strong>same segment</strong> — one other than yours. A shift of <em>d</em> across a weight gap
            of <em>g</em> moves the digest by <em>g·d</em>, so a repair only exists when the gap{' '}
            <strong>divides</strong> the residual and the resulting shift fits inside both lines'
            tolerances. Which means <strong>how</strong> you absorb the skim is not bookkeeping — it is
            your lever. Shifting the split one credit from one line to the other moves the residual by the
            difference of their weights.
          </p>
          <p style={{ ...mono, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            Each line shows how far its paper trail can be bent; some cannot be bent at all. At most{' '}
            <strong>{live?.editBudget ?? 5} lines</strong> may differ from the notarised copy, your own
            included — every altered line is a line in the audit's diff.
            {(live?.sealTolerance ?? 0) > 0
              ? ` Your hashing tool is good enough for a near-collision: the digest may miss by up to ${live?.sealTolerance} and still pass.`
              : ' Your hashing tool finds no collisions, so the digest has to match to the credit.'}
          </p>
          <p style={{ ...mono, color: 'var(--text-muted)', margin: '0 0 0.8rem' }}>
            The books arrive when you open them. The clock is already running — about{' '}
            {live?.readingAllowanceSec ?? 35}s of it is set aside for this.
          </p>
          <button
            className="primary"
            onClick={() => void send({ kind: 'begin' })}
            style={{ fontSize: '0.85rem', padding: '0.4rem 1rem' }}
          >
            Open the ledger
          </button>
        </div>
      </div>
    );
  }

  const ok = live?.ok ?? false;
  const sealDelta = live?.sealDelta ?? 0;
  const sealOk = Math.abs(sealDelta) <= (live?.sealTolerance ?? 0);
  const skimDelta = live?.skimDelta ?? 0;
  const touched = live?.touched ?? 0;
  const budget = live?.editBudget ?? 5;

  return (
    <div style={{ fontFamily: 'monospace' }}>
      {/* The audit's own readouts. Fixed widths, so a figure going 9 → -1204 moves nothing. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', whiteSpace: 'nowrap',
        border: `1px solid ${ok ? '#166534' : 'var(--border-color)'}`, borderRadius: 4,
        padding: '0.5rem 0.7rem', marginBottom: '0.55rem', background: '#0d1117',
      }}>
        <span style={{ ...mono, minWidth: '10.5rem' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>YOUR LINE </span>
          <strong style={{ color: skimDelta === 0 ? '#4ade80' : '#fbbf24' }}>
            {skimDelta === 0 ? `+${live?.skim ?? 0} exactly` : `off by ${skimDelta > 0 ? '+' : ''}${skimDelta}`}
          </strong>
        </span>
        <span style={{ ...mono, minWidth: '13rem' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>DIGEST </span>
          <strong style={{ color: sealOk ? '#4ade80' : '#f87171' }}>
            {sealDelta === 0 ? 'identical' : `out by ${sealDelta > 0 ? '+' : ''}${sealDelta}`}
          </strong>
          {(live?.sealTolerance ?? 0) > 0 && (
            <span style={{ color: '#4b5563' }}> (±{live?.sealTolerance} allowed)</span>
          )}
        </span>
        <span style={{
          ...mono, minWidth: '8rem',
          color: touched > budget ? '#f87171' : touched === budget ? '#fbbf24' : 'var(--text-muted)',
        }}>
          <strong>{touched}</strong> / {budget} lines altered
        </span>
        <span style={{ ...small, minWidth: '6.5rem', color: ok ? '#4ade80' : '#4b5563', fontWeight: 700 }}>
          {ok ? 'AUDIT CLEAN' : ''}
        </span>
      </div>

      {/* Wide content scrolls inside its own container rather than the page. */}
      <div style={{ overflowX: 'auto', marginBottom: '0.55rem' }}>
        <div style={{ minWidth: 560 }}>
          {(live?.segments ?? []).map((seg, si) => (
            <div key={seg.name} style={{
              border: `1px solid ${seg.delta !== 0 ? '#7f1d1d' : seg.mine ? '#2b3444' : '#1c2029'}`,
              borderRadius: 4, marginBottom: '0.35rem', background: seg.mine ? '#0d1117' : '#0a0d12',
            }}>
              <div style={{
                display: 'flex', gap: '0.6rem', alignItems: 'baseline', whiteSpace: 'nowrap',
                padding: '0.3rem 0.55rem', borderBottom: '1px solid #1c2029',
              }}>
                <strong style={{ ...small, minWidth: '9rem', color: seg.mine ? '#60a5fa' : '#9aa4b2' }}>
                  {seg.name}{seg.mine ? ' · yours' : ''}
                </strong>
                <span style={{ ...small, minWidth: '9.5rem', color: 'var(--text-muted)' }}>
                  subtotal {seg.notarised}
                </span>
                <span style={{ ...small, minWidth: '7rem', color: seg.delta === 0 ? '#4ade80' : '#f87171' }}>
                  {seg.delta === 0 ? 'restored' : `out by ${seg.delta > 0 ? '+' : ''}${seg.delta}`}
                </span>
              </div>

              {entries.map((e, i) => {
                if (e.segment !== si) return null;
                const locked = !e.isYours && e.tolerance === 0;
                const d = (amounts[i] ?? e.notarised) - e.notarised;
                return (
                  <div key={e.name} style={{
                    display: 'flex', gap: '0.4rem', alignItems: 'center', whiteSpace: 'nowrap',
                    padding: '0.18rem 0.55rem',
                    background: e.isYours ? '#131c2b' : 'transparent',
                  }}>
                    <span style={{ ...small, minWidth: '10.5rem', color: e.isYours ? '#93c5fd' : '#cbd5e1' }}>
                      {e.name}
                    </span>
                    <span style={{ ...small, minWidth: '2.4rem', color: '#fbbf24' }}>w{e.weight}</span>
                    <span style={{ ...small, minWidth: '4rem', color: '#4b5563', textAlign: 'right' }}>
                      {e.notarised}
                    </span>
                    <input
                      type="number"
                      value={amounts[i] ?? e.notarised}
                      onChange={ev => setAt(i, Number(ev.target.value) || 0)}
                      disabled={locked}
                      style={{
                        ...small, width: '5.6rem', padding: '0.1rem 0.3rem', textAlign: 'right',
                        borderColor: e.overTolerance ? '#f87171' : undefined,
                        opacity: locked ? 0.4 : 1,
                      }}
                    />
                    <span style={{
                      ...small, minWidth: '4.2rem', textAlign: 'right',
                      color: d === 0 ? '#4b5563' : e.overTolerance ? '#f87171' : '#4ade80',
                    }}>
                      {d === 0 ? '—' : `${d > 0 ? '+' : ''}${d}`}
                    </span>
                    <span style={{ ...small, minWidth: '5.5rem', color: locked ? '#6b7280' : 'var(--text-muted)' }}>
                      {locked ? 'notarised' : `±${e.tolerance}`}
                    </span>
                    {!locked && (
                      <>
                        <button
                          onClick={() => setAt(i, (amounts[i] ?? e.notarised) - 1)}
                          style={{ ...small, padding: '0 0.35rem' }}
                        >−</button>
                        <button
                          onClick={() => setAt(i, (amounts[i] ?? e.notarised) + 1)}
                          style={{ ...small, padding: '0 0.35rem' }}
                        >+</button>
                      </>
                    )}
                    {e.isYours && (
                      <button
                        onClick={() => setAt(i, e.notarised + (live?.skim ?? 0))}
                        style={{ ...small, padding: '0 0.4rem' }}
                        title="Credit yourself the whole skim"
                      >+{live?.skim ?? 0}</button>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Reserved slot, so a refusal never shifts the buttons under it. */}
      <div style={{ minHeight: '1.3rem', marginBottom: '0.25rem', overflow: 'hidden' }}>
        {note && <span style={{ ...small, color: '#fbbf24' }}>{note}</span>}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          className="primary"
          onClick={() => {
            if (!ok) {
              setNote('The audit would catch it as it stands — file it anyway only if you are out of time.');
              return;
            }
            void send({ kind: 'commit', amounts });
          }}
          style={{ fontSize: '0.85rem', padding: '0.35rem 0.9rem' }}
          title="File the books"
        >
          File the books
        </button>
        <button
          onClick={() => onSubmit({ amounts })}
          style={{ ...small, padding: '0.25rem 0.6rem' }}
          title="File it as it stands, clean or not"
        >
          File it anyway
        </button>
        <button
          onClick={() => setAmounts(entries.map(e => e.notarised))}
          style={{ ...small, padding: '0.25rem 0.6rem' }}
          title="Put every line back to the notarised figure"
        >
          Start over
        </button>
        <span style={{ ...small, color: '#4b5563' }}>
          digest = Σ (weight × amount) over all fifteen lines
        </span>
      </div>
    </div>
  );
}
